import { Box, Container, Typography } from "@mui/material";
import OptimizedImage from "../ui/OptimizedImage";
import { aboutTimelineItems } from "../../data/aboutTimeline";

const colors = {
    sand: "#f7f1e8",
    cream: "#fffaf2",
    paper: "rgba(255, 250, 242, 0.86)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    blue: "#1d76bd",
};

function AboutSection() {
    return (
        <Box
            component="section"
            id="ueber-uns"
            sx={{
                position: "relative",
                overflow: "hidden",
                py: {
                    xs: 8,
                    md: 13,
                },
                background:
                    "linear-gradient(180deg, #fffaf2 0%, #f7f1e8 52%, #f2eadc 100%)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: {
                        xs: 90,
                        md: 120,
                    },
                    right: {
                        xs: -160,
                        md: -110,
                    },
                    width: {
                        xs: 300,
                        md: 460,
                    },
                    height: {
                        xs: 300,
                        md: 460,
                    },
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(155,118,83,0.16) 0%, rgba(155,118,83,0) 68%)",
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    left: {
                        xs: -90,
                        md: -130,
                    },
                    bottom: {
                        xs: 280,
                        md: 180,
                    },
                    width: {
                        xs: 220,
                        md: 340,
                    },
                    height: {
                        xs: 220,
                        md: 340,
                    },
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(47,145,184,0.09) 0%, rgba(47,145,184,0) 70%)",
                    pointerEvents: "none",
                }}
            />

            <Container
                maxWidth={false}
                sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 1180,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "0.95fr 1.05fr",
                        },
                        gap: {
                            xs: 5,
                            md: 8,
                        },
                        alignItems: "center",
                        mb: {
                            xs: 7,
                            md: 10,
                        },
                    }}
                >
                    <Box>
                        <Typography
                            component="p"
                            sx={{
                                mb: 1.3,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: "0.17em",
                                textTransform: "uppercase",
                                color: colors.brown,
                            }}
                        >
                            Über uns
                        </Typography>

                        <Typography
                            component="h2"
                            sx={{
                                mb: 3,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.35rem",
                                    sm: "3rem",
                                    md: "4rem",
                                },
                                lineHeight: 1,
                                fontWeight: 700,
                                letterSpacing: "-0.055em",
                                color: colors.ink,
                            }}
                        >
                            Ein Lädchen mit über 100 Jahren Geschichte
                        </Typography>

                        <Typography
                            sx={{
                                mb: 2.5,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 17,
                                    md: 19,
                                },
                                lineHeight: 1.78,
                                color: colors.muted,
                            }}
                        >
                            Das Hooksieler Lädchen ist seit Generationen ein Ort für
                            Begegnung, Beratung und besondere Dinge. Was einst mit
                            Schneiderei, Textilien und Haushaltswaren begann, ist heute ein
                            liebevoll geführtes Geschäft für Mode, Tee und Mee(h)r.
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 17,
                                    md: 19,
                                },
                                lineHeight: 1.78,
                                color: colors.muted,
                            }}
                        >
                            Seit 2019 führt Claudia Don das Lädchen mit frischem Wind,
                            viel Herzblut und einem Blick für sportlich-schicke Mode,
                            friesische Teespezialitäten und ausgesuchte Geschenkideen.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            position: "relative",
                            p: {
                                xs: 1,
                                md: 1.3,
                            },
                            borderRadius: {
                                xs: 5,
                                md: 7,
                            },
                            background:
                                "linear-gradient(135deg, rgba(255,255,255,0.88), rgba(225,204,174,0.7))",
                            boxShadow: "0 28px 80px rgba(78, 54, 30, 0.18)",
                            transform: {
                                xs: "none",
                                md: "rotate(1deg)",
                            },

                            "&::before": {
                                content: '""',
                                position: "absolute",
                                inset: {
                                    xs: 10,
                                    md: 14,
                                },
                                border: "1px solid rgba(111,81,54,0.22)",
                                borderRadius: {
                                    xs: 4,
                                    md: 6,
                                },
                                pointerEvents: "none",
                                zIndex: 3,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                overflow: "hidden",
                                borderRadius: {
                                    xs: 4,
                                    md: 6,
                                },
                                backgroundColor: "#d5c2a3",
                            }}
                        >
                            <OptimizedImage
                                imageKey="src/assets/images/bornefeld.jpg"
                                alt="Historisches Foto des Hauses Johann Bornefeld in Hooksiel"
                                sizes="(max-width: 900px) 100vw, 50vw"
                                sx={{
                                    width: "100%",
                                    aspectRatio: {
                                        xs: "4 / 3",
                                        md: "5 / 4",
                                    },
                                }}
                                imgSx={{
                                    width: "100%",
                                    height: "100%",
                                    filter: "sepia(0.28) contrast(1.02) saturate(0.9)",
                                }}
                                objectFit="cover"
                                objectPosition="center"
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 1,
                                    background:
                                        "linear-gradient(180deg, rgba(255,250,242,0.02) 0%, rgba(54,35,20,0.34) 100%)",
                                    pointerEvents: "none",
                                }}
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    zIndex: 2,
                                    left: {
                                        xs: 18,
                                        md: 24,
                                    },
                                    bottom: {
                                        xs: 18,
                                        md: 24,
                                    },
                                    maxWidth: 340,
                                    px: 2,
                                    py: 1.4,
                                    borderRadius: 3,
                                    backgroundColor: "rgba(255,250,242,0.9)",
                                    backdropFilter: "blur(8px)",
                                    boxShadow: "0 12px 34px rgba(0,0,0,0.16)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 12,
                                        fontWeight: 900,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: colors.brown,
                                    }}
                                >
                                    Historische Aufnahme
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.3,
                                        fontFamily: '"Quicksand", sans-serif',
                                        fontSize: {
                                            xs: 18,
                                            md: 20,
                                        },
                                        fontWeight: 700,
                                        color: colors.ink,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Familie Bornefeld vor dem Geschäft
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        maxWidth: 880,
                        mx: "auto",
                        pl: {
                            xs: 3,
                            md: 0,
                        },

                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 8,
                            bottom: 8,
                            left: {
                                xs: 14,
                                md: "50%",
                            },
                            width: 2,
                            transform: {
                                xs: "none",
                                md: "translateX(-50%)",
                            },
                            background:
                                "linear-gradient(180deg, rgba(155,118,83,0), rgba(155,118,83,0.55), rgba(47,145,184,0.25), rgba(155,118,83,0))",
                            borderRadius: 999,
                        },
                    }}
                >
                    {aboutTimelineItems.map((item, index) => {
                        const isLeft = index % 2 === 0;

                        return (
                            <Box
                                key={item.year}
                                sx={{
                                    position: "relative",
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "1fr 1fr",
                                    },
                                    mb: {
                                        xs: 3,
                                        md: 1.5,
                                    },
                                    minHeight: {
                                        md: 185,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        gridColumn: {
                                            xs: "1",
                                            md: isLeft ? "1 / 2" : "2 / 3",
                                        },
                                        pr: {
                                            md: isLeft ? 5 : 0,
                                        },
                                        pl: {
                                            md: isLeft ? 0 : 5,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "relative",
                                            p: {
                                                xs: 2.5,
                                                md: 3,
                                            },
                                            borderRadius: 4,
                                            backgroundColor: colors.paper,
                                            backdropFilter: "blur(10px)",
                                            boxShadow:
                                                "0 18px 52px rgba(78, 54, 30, 0.08)",
                                            border: "1px solid rgba(255,255,255,0.82)",

                                            "&::after": {
                                                content: {
                                                    xs: "none",
                                                    md: '""',
                                                },
                                                position: "absolute",
                                                top: 32,
                                                [isLeft ? "right" : "left"]: -8,
                                                width: 16,
                                                height: 16,
                                                backgroundColor: colors.paper,
                                                transform: "rotate(45deg)",
                                                borderTop: "1px solid rgba(255,255,255,0.82)",
                                                borderRight: isLeft
                                                    ? "1px solid rgba(255,255,255,0.82)"
                                                    : "none",
                                                borderLeft: !isLeft
                                                    ? "1px solid rgba(255,255,255,0.82)"
                                                    : "none",
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                mb: 1,
                                                fontFamily: '"Nunito Sans", sans-serif',
                                                fontSize: 13,
                                                fontWeight: 900,
                                                letterSpacing: "0.12em",
                                                textTransform: "uppercase",
                                                color:
                                                    index === aboutTimelineItems.length - 1
                                                        ? colors.blue
                                                        : colors.brown,
                                            }}
                                        >
                                            {item.year}
                                        </Typography>

                                        <Typography
                                            component="h3"
                                            sx={{
                                                mb: 1.2,
                                                fontFamily: '"Quicksand", sans-serif',
                                                fontSize: {
                                                    xs: "1.4rem",
                                                    md: "1.55rem",
                                                },
                                                lineHeight: 1.15,
                                                fontWeight: 700,
                                                color: colors.ink,
                                            }}
                                        >
                                            {item.title}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontFamily: '"Nunito Sans", sans-serif',
                                                fontSize: {
                                                    xs: 15.8,
                                                    md: 16,
                                                },
                                                lineHeight: 1.68,
                                                color: colors.muted,
                                            }}
                                        >
                                            {item.text}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: {
                                            xs: 26,
                                            md: 31,
                                        },
                                        left: {
                                            xs: -31,
                                            md: "50%",
                                        },
                                        transform: {
                                            xs: "none",
                                            md: "translateX(-50%)",
                                        },
                                        width: {
                                            xs: 28,
                                            md: 34,
                                        },
                                        height: {
                                            xs: 28,
                                            md: 34,
                                        },
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor:
                                            index === aboutTimelineItems.length - 1
                                                ? colors.blue
                                                : colors.cream,
                                        border: `2px solid ${
                                            index === aboutTimelineItems.length - 1
                                                ? colors.blue
                                                : colors.brown
                                        }`,
                                        boxShadow:
                                            "0 8px 24px rgba(78, 54, 30, 0.16)",
                                        zIndex: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 9,
                                            height: 9,
                                            borderRadius: "50%",
                                            backgroundColor:
                                                index === aboutTimelineItems.length - 1
                                                    ? "#fff"
                                                    : colors.brown,
                                        }}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                <Box
                    sx={{
                        mt: {
                            xs: 5,
                            md: 7,
                        },
                        mx: "auto",
                        maxWidth: 850,
                        p: {
                            xs: 3,
                            md: 4.5,
                        },
                        borderRadius: {
                            xs: 5,
                            md: 6,
                        },
                        background:
                            "linear-gradient(135deg, #8f6b4a 0%, #6f5136 100%)",
                        color: "#fff",
                        textAlign: "center",
                        boxShadow: "0 24px 70px rgba(78,54,30,0.22)",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Quicksand", sans-serif',
                            fontSize: {
                                xs: "1.55rem",
                                md: "2.2rem",
                            },
                            lineHeight: 1.25,
                            fontWeight: 700,
                            letterSpacing: "-0.035em",
                        }}
                    >
                        Eine Anlaufstelle für Einheimische und Gäste — mit Zeit für
                        Beratung, Mode und einen netten Klönschnack.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}

export default AboutSection;